import React, { useEffect, useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/site/Reveal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
    const [key, setKey] = useState(() => localStorage.getItem("nyra_admin_key") || "");
    const [authed, setAuthed] = useState(false);
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");

    const fetchAll = async (k) => {
        setLoading(true);
        try {
            const [l, s] = await Promise.all([
                axios.get(`${API}/leads`, { params: { admin_key: k } }),
                axios.get(`${API}/leads/stats`, { params: { admin_key: k } }),
            ]);
            setLeads(l.data);
            setStats(s.data);
            setAuthed(true);
            localStorage.setItem("nyra_admin_key", k);
        } catch (e) {
            setAuthed(false);
            const msg = e?.response?.data?.detail || "Access denied";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (key) fetchAll(key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = (e) => {
        e.preventDefault();
        fetchAll(key);
    };

    const logout = () => {
        localStorage.removeItem("nyra_admin_key");
        setKey("");
        setAuthed(false);
        setLeads([]);
        setStats(null);
    };

    const filtered = leads.filter(
        (l) =>
            !q ||
            [l.name, l.work_email, l.phone, l.company, l.industry, l.use_case, l.message]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(q.toLowerCase()),
    );

    if (!authed) {
        return (
            <div className="min-h-[70vh] flex items-center bg-warm">
                <div className="container-x max-w-md">
                    <Reveal>
                        <div className="text-sm text-slate-500 mb-3">Admin</div>
                        <h1 className="heading-hero text-4xl md:text-5xl mb-8">Enter admin key.</h1>
                        <form onSubmit={submit} className="gleap-card p-6">
                            <input
                                type="password"
                                value={key}
                                onChange={(e) => setKey(e.target.value)}
                                placeholder="Admin key"
                                className="w-full px-4 py-3 rounded-full border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:border-slate-900 mb-4"
                                data-testid="admin-key-input"
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="pill-dark w-full justify-center"
                                data-testid="admin-key-submit"
                                disabled={loading}
                            >
                                {loading ? "Verifying…" : "Access dashboard"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-warm">
            <section className="py-16">
                <div className="container-x">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                        <div>
                            <div className="text-sm text-slate-500 mb-2">Lead dashboard</div>
                            <h1 className="heading-section text-3xl md:text-5xl">Captured leads</h1>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search leads…"
                                className="text-sm px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-900 focus:outline-none focus:border-slate-900 w-full sm:w-auto"
                                style={{ maxWidth: 220 }}
                                data-testid="admin-search"
                            />
                            <button
                                onClick={() => fetchAll(key)}
                                className="pill-ghost"
                                style={{ padding: "10px 18px", fontSize: 13 }}
                                data-testid="admin-refresh"
                            >
                                <i className="fa-solid fa-arrows-rotate text-xs" /> Refresh
                            </button>
                            <button
                                onClick={logout}
                                className="pill-ghost"
                                style={{ padding: "10px 18px", fontSize: 13 }}
                                data-testid="admin-logout"
                            >
                                <i className="fa-solid fa-right-from-bracket text-xs" /> Logout
                            </button>
                        </div>
                    </div>

                    {stats && (
                        <div className="grid md:grid-cols-4 gap-4 mb-8">
                            <div className="gleap-card p-6">
                                <div className="text-xs uppercase tracking-[0.15em] text-slate-500">
                                    Total leads
                                </div>
                                <div
                                    className="heading-stat text-5xl text-slate-900 mt-2"
                                    data-testid="admin-total-leads"
                                >
                                    {stats.total}
                                </div>
                            </div>
                            {stats.by_industry.slice(0, 3).map((row, idx) => (
                                <div key={`${row.industry}-${idx}`} className="gleap-card p-6">
                                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500">
                                        {row.industry}
                                    </div>
                                    <div
                                        className="heading-stat text-5xl text-slate-900 mt-2"
                                    >
                                        {row.count}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="gleap-card" style={{ padding: 0 }}>
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[1000px]" data-testid="admin-leads-table">
                            <thead>
                                <tr className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                                    <th className="p-4">Name</th>
                                    <th className="p-4">Email</th>
                                    <th className="p-4">Phone</th>
                                    <th className="p-4">Company</th>
                                    <th className="p-4">Industry</th>
                                    <th className="p-4">Interested in</th>
                                    <th className="p-4">Message</th>
                                    <th className="p-4">Source</th>
                                    <th className="p-4">Received</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={9}
                                            className="p-10 text-center text-slate-500"
                                            data-testid="admin-empty"
                                        >
                                            No leads yet.
                                        </td>
                                    </tr>
                                )}
                                {filtered.map((l) => (
                                    <tr
                                        key={l.id}
                                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                                    >
                                        <td className="p-4 text-slate-900 font-medium">{l.name}</td>
                                        <td className="p-4 text-slate-700">
                                            <a
                                                href={`mailto:${l.work_email}`}
                                                className="hover:text-slate-900"
                                            >
                                                {l.work_email}
                                            </a>
                                        </td>
                                        <td className="p-4 text-slate-700">{l.phone || "—"}</td>
                                        <td className="p-4 text-slate-700">{l.company}</td>
                                        <td className="p-4 text-slate-600">{l.industry || "—"}</td>
                                        <td className="p-4 text-slate-600">{l.use_case || "—"}</td>
                                        <td
                                            className="p-4 text-slate-600 max-w-xs truncate"
                                            title={l.message || ""}
                                        >
                                            {l.message || "—"}
                                        </td>
                                        <td className="p-4 text-slate-500 font-mono text-xs">
                                            {l.source_page || "/"}
                                        </td>
                                        <td className="p-4 text-slate-500 font-mono text-xs">
                                            {new Date(l.created_at).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
